import { Box, Paper, Typography } from '@mui/material';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { LoadingSkeleton } from '../../../../common/components';
import GerencialesPieTableSection from '../GerencialesPieTableSection';
import { PIE_ALLOWED_COLORS } from '../../constants/gerencialesConstants';
import { formatCurrency, formatNumber } from '../../../../utils/formatUtils';

/**
 * Renderiza el informe de pagos por entidad legal.
 * @param {Object} props - Props del componente.
 * @param {boolean} props.loading - Estado de carga.
 * @param {Array<Object>|null} props.results - Resultados del reporte.
 * @param {Object|null} props.header - Header con metadata.
 * @returns {JSX.Element|null} Informe de pagos.
 */
const PaymentsReport = ({ loading, results, header }) => {
  if (loading) {
    return <LoadingSkeleton />;
  }

  if (!results) {
    return null;
  }

  const normalizedResults = Array.isArray(results) ? { table: results } : results || {};
  const rows = Array.isArray(normalizedResults.table) ? normalizedResults.table : [];
  const interannual = normalizedResults.interannual || {};

  const palette = PIE_ALLOWED_COLORS;
  const pieData = rows.map((row, index) => ({
    name: row?.legal_entity_name ?? 'Sin dato',
    value: Number(row?.count ?? 0),
    fill: palette[index % palette.length],
  }));

  const currentYear = interannual?.current_year?.year;
  const previousYear = interannual?.previous_year?.year;
  const twoYearsAgo = interannual?.two_years_ago?.year;

  const monthLabels = [
    'Ene',
    'Feb',
    'Mar',
    'Abr',
    'May',
    'Jun',
    'Jul',
    'Ago',
    'Sep',
    'Oct',
    'Nov',
    'Dic',
  ];

  const buildLineData = (currentSeries = [], previousSeries = []) =>
    monthLabels.map((label, index) => ({
      label,
      current: Number(currentSeries[index]?.value ?? 0),
      previous: Number(previousSeries[index]?.value ?? 0),
    }));

  const couponsYoY = buildLineData(
    interannual?.current_year?.monthly_counts,
    interannual?.previous_year?.monthly_counts
  );
  const couponsAmountYoY = buildLineData(
    interannual?.current_year?.monthly_amounts,
    interannual?.previous_year?.monthly_amounts
  );
  const lastYearCouponsYoY = buildLineData(
    interannual?.previous_year?.monthly_counts,
    interannual?.two_years_ago?.monthly_counts
  );
  const lastYearAmountYoY = buildLineData(
    interannual?.previous_year?.monthly_amounts,
    interannual?.two_years_ago?.monthly_amounts
  );

  const ChartCard = ({ title, chartId, children }) => (
    <Paper elevation={1} sx={{ mb: 1, mt: 2 }}>
      <Box
        sx={{
          backgroundColor: '#3b3f46',
          color: 'white',
          px: 2.5,
          py: 1.5,
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4,
          fontSize: 30,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          {title}
        </Typography>
      </Box>
      <Box id={chartId} sx={{ p: 2 }}>
        {children}
      </Box>
    </Paper>
  );

  const renderLineChart = (data, formatter, currentLabel, previousLabel, colors) => (
    <Box sx={{ height: { xs: 260, md: 300 } }}>
      {data.some(
        (item) => Number(item.current ?? 0) > 0 || Number(item.previous ?? 0) > 0
      ) ? (
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="label" />
            <YAxis tickFormatter={formatter} />
            <RechartsTooltip formatter={formatter} />
            <Legend />
            <Line
              type="monotone"
              dataKey="previous"
              name={previousLabel}
              stroke={colors[0]}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="current"
              name={currentLabel}
              stroke={colors[1]}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Sin datos para graficar
        </Typography>
      )}
    </Box>
  );

  return (
    <>
      <GerencialesPieTableSection
        title="Pagos por entidad legal"
        columns={[
          { key: 'legal_entity_name', label: 'Entidad legal', align: 'left' },
          { key: 'count', label: 'Pagos', align: 'center' },
        ]}
        rows={rows}
        pieData={pieData}
        totalLabel="Total pagos"
        totalCount={header?.total_payments ?? 0}
        chartId="gerenciales-payments-report-legal-entity"
      />

      <ChartCard
        title={`Variacion interanual de cupones cobrados (${currentYear} vs ${previousYear})`}
        chartId="gerenciales-payments-yoy-coupons-current"
      >
        {renderLineChart(
          couponsYoY,
          (value) => formatNumber(value, { decimals: 0 }),
          `${currentYear ?? 'Actual'}`,
          `${previousYear ?? 'Anterior'}`,
          [PIE_ALLOWED_COLORS[2], PIE_ALLOWED_COLORS[0]]
        )}
      </ChartCard>

      <ChartCard
        title={`Variacion interanual de importe de cupones (${currentYear} vs ${previousYear})`}
        chartId="gerenciales-payments-yoy-amount-current"
      >
        {renderLineChart(
          couponsAmountYoY,
          (value) => formatCurrency(value),
          `${currentYear ?? 'Actual'}`,
          `${previousYear ?? 'Anterior'}`,
          [PIE_ALLOWED_COLORS[3], PIE_ALLOWED_COLORS[1]]
        )}
      </ChartCard>

      <ChartCard
        title={`Variacion interanual de cupones cobrados (${previousYear} vs ${twoYearsAgo})`}
        chartId="gerenciales-payments-yoy-coupons-previous"
      >
        {renderLineChart(
          lastYearCouponsYoY,
          (value) => formatNumber(value, { decimals: 0 }),
          `${previousYear ?? 'Anterior'}`,
          `${twoYearsAgo ?? 'Anteanterior'}`,
          [PIE_ALLOWED_COLORS[2], PIE_ALLOWED_COLORS[0]]
        )}
      </ChartCard>

      <ChartCard
        title={`Variacion interanual de importe de cupones (${previousYear} vs ${twoYearsAgo})`}
        chartId="gerenciales-payments-yoy-amount-previous"
      >
        {renderLineChart(
          lastYearAmountYoY,
          (value) => formatCurrency(value),
          `${previousYear ?? 'Anterior'}`,
          `${twoYearsAgo ?? 'Anteanterior'}`,
          [PIE_ALLOWED_COLORS[3], PIE_ALLOWED_COLORS[1]]
        )}
      </ChartCard>
    </>
  );
};

export default PaymentsReport;
